import React, { useEffect, useState } from "react";
import {
  Alert, Button, Card, Checkbox, Col, Form, Input, Modal,
  Popconfirm, Row, Space, Spin, Switch, Table, Tag, Tooltip, Typography,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  SafetyCertificateOutlined, TeamOutlined,
} from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

// Módulos del sistema con su ícono/label
const MODULOS = [
  { key: "inventario",    label: "📦 Inventario" },
  { key: "ganado",        label: "🐄 Animales / Ganado" },
  { key: "leche",         label: "🥛 Registro de Leche" },
  { key: "cultivos",      label: "🌱 Cultivos" },
  { key: "reportes",      label: "📊 Estadísticas / Reportes" },
  { key: "usuarios",      label: "👤 Gestión de Usuarios" },
  { key: "configuracion", label: "⚙️ Configuración" },
];

const ACCIONES = [
  { key: "puedeVer",      label: "Ver" },
  { key: "puedeCrear",    label: "Crear" },
  { key: "puedeEditar",   label: "Editar" },
  { key: "puedeEliminar", label: "Eliminar" },
];

// Estado inicial de la cuadrícula de permisos (todo en false)
const accesoVacio = () =>
  MODULOS.map(m => ({
    modulo: m.key,
    puedeVer: false,
    puedeCrear: false,
    puedeEditar: false,
    puedeEliminar: false,
  }));

export default function FrmRoles() {
  const [roles, setRoles] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [alerta, setAlerta] = useState(null);

  // Modal
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [rolEditando, setRolEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [form] = Form.useForm();
  const [accesos, setAccesos] = useState(accesoVacio());

  const cargar = async () => {
    try {
      setCargando(true);
      const resp = await axios.get(`${API_URL}/Rol`);
      const lista = Array.isArray(resp.data?.$values) ? resp.data.$values
        : Array.isArray(resp.data) ? resp.data : [];
      setRoles(lista);
    } catch {
      setAlerta({ tipo: "error", texto: "Error al cargar roles." });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  // Construir cuadrícula desde accesos del rol — solo toma los campos necesarios
  const accesoDesdeRol = (rol) => {
    const base = accesoVacio();
    const accs = Array.isArray(rol.accesos?.$values) ? rol.accesos.$values
      : Array.isArray(rol.accesos) ? rol.accesos : [];
    return base.map(b => {
      const found = accs.find(a => a.modulo === b.modulo);
      if (!found) return b;
      // Solo copiamos los campos de permisos, ignoramos $id, id, rolId, etc.
      return {
        modulo:        found.modulo,
        puedeVer:      found.puedeVer      ?? false,
        puedeCrear:    found.puedeCrear    ?? false,
        puedeEditar:   found.puedeEditar   ?? false,
        puedeEliminar: found.puedeEliminar ?? false,
      };
    });
  };

  const abrirCrear = () => {
    setModoEdicion(false);
    setRolEditando(null);
    form.resetFields();
    setAccesos(accesoVacio());
    setModalAbierto(true);
  };

  const abrirEditar = (rol) => {
    setModoEdicion(true);
    setRolEditando(rol);
    form.setFieldsValue({
      nombre: rol.nombre,
      descripcion: rol.descripcion,
      esAdministrador: rol.esAdministrador,
    });
    setAccesos(accesoDesdeRol(rol));
    setModalAbierto(true);
  };

  const toggleAcceso = (moduloKey, accionKey, valor) => {
    setAccesos(prev => prev.map(a =>
      a.modulo === moduloKey ? { ...a, [accionKey]: valor } : a
    ));
  };

  // Marcar/desmarcar toda una fila (módulo)
  const toggleFila = (moduloKey, marcar) => {
    setAccesos(prev => prev.map(a =>
      a.modulo === moduloKey
        ? { ...a, puedeVer: marcar, puedeCrear: marcar, puedeEditar: marcar, puedeEliminar: marcar }
        : a
    ));
  };

  // Marcar/desmarcar toda una columna (acción)
  const toggleColumna = (accionKey, marcar) => {
    setAccesos(prev => prev.map(a => ({ ...a, [accionKey]: marcar })));
  };

  const guardar = async () => {
    try {
      const values = await form.validateFields();
      setGuardando(true);

      // Limpiar objetos — quitar $id y otros campos del servidor antes de enviar
      const accesosLimpios = accesos.map(a => ({
        modulo:        a.modulo,
        puedeVer:      a.puedeVer      ?? false,
        puedeCrear:    a.puedeCrear    ?? false,
        puedeEditar:   a.puedeEditar   ?? false,
        puedeEliminar: a.puedeEliminar ?? false,
      }));

      const body = {
        nombre:          values.nombre,
        descripcion:     values.descripcion ?? null,
        esAdministrador: values.esAdministrador ?? false,
        accesos:         accesosLimpios,
      };

      if (modoEdicion) {
        await axios.put(`${API_URL}/Rol/${rolEditando.id}`, body);
        setAlerta({ tipo: "success", texto: `✅ Rol "${values.nombre}" actualizado.` });
      } else {
        await axios.post(`${API_URL}/Rol`, body);
        setAlerta({ tipo: "success", texto: `✅ Rol "${values.nombre}" creado.` });
      }

      setModalAbierto(false);
      await cargar();
    } catch (err) {
      if (err?.errorFields) return;
      const data = err?.response?.data;
      let msg = "Error al guardar.";
      if (data?.errors) {
        // Errores de validación de ASP.NET Core
        msg = Object.values(data.errors).flat().join('. ');
      } else if (data?.mensaje) {
        msg = data.mensaje;
      } else if (data?.title) {
        msg = data.title;
      } else if (err?.message) {
        msg = err.message;
      }
      setAlerta({ tipo: "error", texto: `❌ ${msg}` });
      console.error("Error guardando rol:", err?.response?.status, data);
    } finally {
      setGuardando(false);
    }
  };

  const eliminar = async (id) => {
    try {
      await axios.delete(`${API_URL}/Rol/${id}`);
      await cargar();
      setAlerta({ tipo: "success", texto: "Rol eliminado." });
    } catch (err) {
      const msg = err?.response?.data?.mensaje ?? "Error al eliminar.";
      setAlerta({ tipo: "error", texto: `❌ ${msg}` });
    }
  };

  const columnas = [
    {
      title: "Rol",
      key: "nombre",
      render: (_, r) => (
        <div>
          <Space>
            <SafetyCertificateOutlined style={{ color: r.esAdministrador ? "#ff4d4f" : "#1677ff" }} />
            <Text strong style={{ fontSize: 15 }}>{r.nombre}</Text>
            {r.esAdministrador && <Tag color="red">Administrador total</Tag>}
          </Space>
          {r.descripcion && (
            <div><Text type="secondary" style={{ fontSize: 12 }}>{r.descripcion}</Text></div>
          )}
        </div>
      ),
    },
    {
      title: "Usuarios",
      dataIndex: "totalUsuarios",
      width: 100,
      align: "center",
      render: v => (
        <Space size={4}>
          <TeamOutlined />
          <Text>{v}</Text>
        </Space>
      ),
    },
    {
      title: "Permisos",
      key: "permisos",
      render: (_, r) => {
        const accs = Array.isArray(r.accesos?.$values) ? r.accesos.$values
          : Array.isArray(r.accesos) ? r.accesos : [];
        const conAcceso = accs.filter(a => a.puedeVer || a.puedeCrear || a.puedeEditar || a.puedeEliminar);
        if (r.esAdministrador)
          return <Tag color="red">Acceso total al sistema</Tag>;
        if (conAcceso.length === 0)
          return <Tag color="default">Sin permisos asignados</Tag>;
        return (
          <Space wrap size={4}>
            {conAcceso.map(a => {
              const mod = MODULOS.find(m => m.key === a.modulo);
              return <Tag key={a.modulo} color="blue">{mod?.label ?? a.modulo}</Tag>;
            })}
          </Space>
        );
      },
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 100,
      align: "center",
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Editar rol y permisos">
            <Button size="small" icon={<EditOutlined />} onClick={() => abrirEditar(r)} />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar este rol?"
            description="Los usuarios asignados quedarán sin rol."
            onConfirm={() => eliminar(r.id)}
            okText="Sí, eliminar" cancelText="No"
            okButtonProps={{ danger: true }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>🔐 Roles y permisos</Title>
          <Text type="secondary">
            Define qué puede hacer cada rol en cada módulo del sistema.
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={abrirCrear}>
          Nuevo rol
        </Button>
      </div>

      {alerta && (
        <Alert type={alerta.tipo} message={alerta.texto} showIcon closable
          onClose={() => setAlerta(null)} style={{ marginBottom: 12 }} />
      )}

      <Spin spinning={cargando}>
        <Table
          rowKey="id"
          columns={columnas}
          dataSource={roles}
          pagination={false}
          size="middle"
          locale={{ emptyText: "Sin roles definidos. Crea el primero." }}
        />
      </Spin>

      {/* ══ Modal crear/editar rol ══ */}
      <Modal
        title={modoEdicion ? `✏️ Editar rol: ${rolEditando?.nombre}` : "➕ Nuevo rol"}
        open={modalAbierto}
        onCancel={() => setModalAbierto(false)}
        onOk={guardar}
        okText={modoEdicion ? "Guardar cambios" : "Crear rol"}
        confirmLoading={guardando}
        width={780}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={14}>
              <Form.Item label="Nombre del rol" name="nombre"
                rules={[{ required: true, message: "Ingrese el nombre del rol." }]}>
                <Input placeholder="Ej: Operador campo, Supervisor, Solo lectura..." />
              </Form.Item>
            </Col>
            <Col xs={24} sm={10}>
              <Form.Item label="¿Administrador total?" name="esAdministrador" valuePropName="checked">
                <Switch
                  checkedChildren="Sí — acceso total"
                  unCheckedChildren="No — usar permisos"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Descripción (opcional)" name="descripcion">
            <Input placeholder="Ej: Puede registrar datos pero no eliminar" />
          </Form.Item>
        </Form>

        {/* ── Cuadrícula de permisos ── */}
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const esAdmin = getFieldValue("esAdministrador");
            return (
              <div style={{ opacity: esAdmin ? 0.4 : 1, pointerEvents: esAdmin ? "none" : "auto" }}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>Permisos por módulo</Text>
                  {esAdmin && <Tag color="red" style={{ marginLeft: 8 }}>Administrador: acceso completo a todo</Tag>}
                </div>

                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f0f5ff" }}>
                        <th style={{ padding: "8px 12px", textAlign: "left", border: "1px solid #d9d9d9", minWidth: 180 }}>
                          Módulo
                        </th>
                        {ACCIONES.map(acc => (
                          <th key={acc.key}
                            style={{ padding: "8px", textAlign: "center", border: "1px solid #d9d9d9", minWidth: 80 }}>
                            <div>{acc.label}</div>
                            <Checkbox
                              style={{ marginTop: 4 }}
                              title={`Marcar/desmarcar todos en "${acc.label}"`}
                              onChange={e => toggleColumna(acc.key, e.target.checked)}
                            />
                          </th>
                        ))}
                        <th style={{ padding: "8px", textAlign: "center", border: "1px solid #d9d9d9", minWidth: 70 }}>
                          Todo
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {MODULOS.map((mod, idx) => {
                        const acc = accesos.find(a => a.modulo === mod.key) ??
                          { puedeVer: false, puedeCrear: false, puedeEditar: false, puedeEliminar: false };
                        const todoMarcado = acc.puedeVer && acc.puedeCrear && acc.puedeEditar && acc.puedeEliminar;
                        return (
                          <tr key={mod.key}
                            style={{ background: idx % 2 === 0 ? "#fafafa" : "#fff" }}>
                            <td style={{ padding: "8px 12px", border: "1px solid #d9d9d9", fontWeight: 500 }}>
                              {mod.label}
                            </td>
                            {ACCIONES.map(accion => (
                              <td key={accion.key}
                                style={{ padding: "8px", textAlign: "center", border: "1px solid #d9d9d9" }}>
                                <Checkbox
                                  checked={acc[accion.key]}
                                  onChange={e => toggleAcceso(mod.key, accion.key, e.target.checked)}
                                />
                              </td>
                            ))}
                            <td style={{ padding: "8px", textAlign: "center", border: "1px solid #d9d9d9" }}>
                              <Checkbox
                                checked={todoMarcado}
                                indeterminate={
                                  (acc.puedeVer || acc.puedeCrear || acc.puedeEditar || acc.puedeEliminar) && !todoMarcado
                                }
                                onChange={e => toggleFila(mod.key, e.target.checked)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 6 }}>
                  💡 Los checkboxes de la cabecera marcan/desmarcan toda la columna. La columna "Todo" marca/desmarca toda la fila.
                </Text>
              </div>
            );
          }}
        </Form.Item>
      </Modal>
    </div>
  );
}
